import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { SelectOption } from '@/components/ui/select'

import { FormChangeFields } from '../../../../shared/form-changes'
import { SaltoFormData } from './salto-space.types'
import { maskSecret } from './use-provider-edit'

export const useSaltoSpaceSchema = (configured: boolean) => {
  const { t } = useTranslation(['organisation'])

  return useMemo(
    () =>
      z
        .object({
          apiKey: z.string(),
          residentAccessLevels: z.array(z.string()),
          residentZones: z.array(z.string()),
          shouldAutoAssignApartmentDoor: z.boolean(),
          shouldAutoAssignUserAccessLevel: z.boolean(),
          url: z.string(),
          visitorAccessLevels: z.array(z.string()),
          visitorZones: z.array(z.string()),
        })
        .superRefine((data, ctx) => {
          if (configured) return

          if (!data.url.trim()) {
            ctx.addIssue({
              code: 'custom',
              message: t('organisation:places.integrations.salto.errors.url'),
              path: ['url'],
            })
          }
          if (!data.apiKey.trim()) {
            ctx.addIssue({
              code: 'custom',
              message: t('organisation:places.integrations.salto.errors.apiKey'),
              path: ['apiKey'],
            })
          }
        }),
    [configured, t],
  )
}

export const useSaltoChangeFields = (
  zoneOptions: SelectOption<string>[],
  levelOptions: SelectOption<string>[],
) => {
  const { t } = useTranslation(['organisation'])

  return useMemo<FormChangeFields<SaltoFormData>>(() => {
    const getLabels = (options: SelectOption<string>[]) => (value: unknown) =>
      (value as string[]).map((id) => options.find((option) => option.value === id)?.label ?? id)
    const formatSwitch = (value: unknown) =>
      value ? t('organisation:places.save.on') : t('organisation:places.save.off')
    const residentGroup = t('organisation:places.integrations.salto.residentAccesses')
    const visitorGroup = t('organisation:places.integrations.salto.visitorAccesses')
    const zoneLabel = t('organisation:places.integrations.salto.zone.label')
    const levelLabel = t('organisation:places.integrations.salto.accessLevel.label')

    return {
      apiKey: {
        format: maskSecret,
        label: t('organisation:places.integrations.salto.apiKeyField.label'),
      },
      residentAccessLevels: {
        items: getLabels(levelOptions),
        label: `${residentGroup}: ${levelLabel}`,
      },
      residentZones: { items: getLabels(zoneOptions), label: `${residentGroup}: ${zoneLabel}` },
      shouldAutoAssignApartmentDoor: {
        format: formatSwitch,
        label: t('organisation:places.integrations.salto.autoAssignResidentDoorAccess'),
      },
      shouldAutoAssignUserAccessLevel: {
        format: formatSwitch,
        label: t('organisation:places.integrations.salto.autoAssignVisitorDoorAccess'),
      },
      url: { label: t('organisation:places.integrations.salto.urlField.label') },
      visitorAccessLevels: {
        items: getLabels(levelOptions),
        label: `${visitorGroup}: ${levelLabel}`,
      },
      visitorZones: { items: getLabels(zoneOptions), label: `${visitorGroup}: ${zoneLabel}` },
    }
  }, [t, zoneOptions, levelOptions])
}
